import { useContext, useCallback } from 'react';
import { compact } from 'underscore';
import Promise from 'bluebird';
import {
  DataServiceContext,
  useEditorStatusContext,
  editorStatusLoadingWrapperForPromise,
} from '@bingads-webui-campaign-react/smart-page-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useFloatingBannerMessage } from '@bingads-webui-campaign-react/smart-component-util';

export const useUploadService = () => {
  const { imageService } = useContext(DataServiceContext);
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const { showWarning } = useFloatingBannerMessage(dispatchEditorStatus);
  const i18n = useContext(I18nContext);

  const uploadImageAsync = useCallback(
    imageData =>
      new Promise((resolve, reject) =>
        imageService
          .upload(imageData)
          .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
          .then(response => imageService.get(response.imageId))
          .then((imageMetaData) => {
            resolve(imageMetaData);
          })
          .catch(reject))
    , [dispatchEditorStatus, imageService]
  );

  const bulkUploadImagesByUrlsAsync = useCallback(
    (images) => {
      const handleErrors = () => {
        showWarning(i18n.getString(_TL_('Data in one or more media has invalid Width, Height or Format.')));
      };

      return imageService.bulkUpsert(images, handleErrors)
        .then((results) => {
          const imageIds = compact(results.map(res => res.imageId)); // filter out errored ones
          const uploadUrls = compact(results.map(res => res.uploadUrl)); // filter out errored ones

          // Add upload url back to image meta data so we could match return result with inputs
          return imageService.batchGet(imageIds)
            .then(imageMetaData =>
              // imageIds and uploadUrls order should match
              imageMetaData.map((data, index) => ({ ...data, uploadUrl: uploadUrls[index] })));
        });
    }
    , [i18n, imageService, showWarning]
  );

  return {
    uploadImageAsync,
    bulkUploadImagesByUrlsAsync,
  };
};
